import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUp, Mic, Volume2, RotateCcw, Loader2 } from "lucide-react";
import { api, useAuth } from "@/hooks/useAuth";
import { KalkiAction, VedicSkyCard } from "@/components/kalki/KalkiAction";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";
import { CompatibilityCard } from "@/components/kalki/CompatibilityCard";
import KalkiIntelligenceDock from "@/components/kalki/KalkiIntelligenceDock";
import ChatSnippetCard from "@/components/kalki/ChatSnippetCard";
import PlanetaryChartSnapshot from "@/components/kalki/PlanetaryChartSnapshot";
import { toast } from "sonner";
import SignInRequired from "@/components/SignInRequired";

/**
 * ChatKrishna — full-page immersive text conversation with Lord Krishna.
 * Route: /app/chat
 *
 * Shares conversation_id with /app/talk via `kalki_{user_id}` so Talk+Chat
 * are ONE memory stream.
 *
 * Scroll rule: page is 100dvh, overflow hidden. Only the messages area
 * internally scrolls.
 *
 * Composition:
 *  Desktop → 55/45 split: conversation (left) + Lord Krishna presence (right).
 *  Mobile  → single column; Krishna is a header moment on empty state only.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const GRAPHITE = "#1C1C1C";
const INK = "#1C1C1C";
const MUTE = "#676773";

const OPENER_CHIPS = ["Life", "Career", "Love", "Money", "Purpose", "What's ahead?"];

/* ========== Divine Light presence (right column, desktop) — pure aura, no photo ========== */
function KrishnaPresence({ status }) {
  // status: "idle" | "thinking" | "insight" | "karma" | "future"
  const orb = useMemo(() => {
    if (status === "thinking") return { glyph: "✦", label: "THINKING" };
    if (status === "insight") return { glyph: "✦", label: "INSIGHT" };
    if (status === "karma") return { glyph: "✓", label: "KARMA" };
    if (status === "future") return { glyph: "✦", label: "FUTURE" };
    return { glyph: "✦", label: null };
  }, [status]);

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {/* Soft outer aura */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: "min(620px, 72%)",
          height: "min(620px, 72%)",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${INDIGO}12 0%, ${FUCHSIA}0a 42%, transparent 72%)`,
          filter: "blur(20px)",
          animation: "chat-breathe 7s ease-in-out infinite",
        }}
      />
      {/* Inner luminous ring */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: "min(340px, 42%)",
          height: "min(340px, 42%)",
          borderRadius: "50%",
          border: `1px solid ${INDIGO}22`,
          boxShadow: `0 0 60px ${INDIGO}22, inset 0 0 60px ${FUCHSIA}11`,
          animation: "chat-breathe 9s ease-in-out infinite",
        }}
      />
      {/* Center intelligence orb */}
      <div
        className="relative z-10 flex flex-col items-center gap-4 select-none"
      >
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 54, height: 54,
            background: "white",
            boxShadow: `0 0 28px ${INDIGO}55, 0 10px 40px rgba(0,0,0,0.06), inset 0 0 0 1px ${INDIGO}22`,
            color: INDIGO,
            fontSize: 22,
            animation: status === "thinking" ? "chat-pulse 1.4s ease-in-out infinite" : "none",
          }}
        >
          {orb.glyph}
        </span>
        {orb.label && (
          <span className="text-[10px] tracking-[0.4em]" style={{ color: INDIGO }}>
            {orb.label}
          </span>
        )}
      </div>

      <style>{`
        @keyframes chat-breathe { 0%,100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.04); opacity: 1; } }
        @keyframes chat-pulse { 0%,100% { box-shadow: 0 0 12px ${INDIGO}44; } 50% { box-shadow: 0 0 36px ${INDIGO}aa, 0 0 56px ${FUCHSIA}44; } }
      `}</style>
    </div>
  );
}

/* ========== One Krishna message block ========== */
function KalkiMessage({ text, onReplay, isReplaying, streaming, experience, vedicSky, onAction, failed, onRetry, lastDomain, onFollowup, practicalStep, onAddKarma, onDoneKarma, karmaSaved, feedback, onFeedback, snippet, onSnippetDismiss, planetaryCard }) {
  if (failed) {
    const hasPartial = !!(text && String(text).trim().length > 0);
    return (
      <div className="w-full group" data-testid="chat-msg-kalki-failed">
        <div className="text-[10px] tracking-[0.34em] opacity-60 mb-2" style={{ color: INDIGO }}>✦ KALKI</div>
        <div
          className="rounded-2xl px-5 py-4"
          style={{
            background: "rgba(255,46,154,0.06)",
            border: "1px solid rgba(255,46,154,0.25)",
            color: INK,
          }}
        >
          {hasPartial && (
            <div style={{ color: INK, fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap" }}
                 data-testid="chat-msg-kalki-partial">
              {text}
            </div>
          )}
          <div style={{ color: MUTE, fontSize: 14, lineHeight: 1.6, marginTop: hasPartial ? 10 : 0 }}>
            {hasPartial ? "KALKI's reply was interrupted." : "KALKI couldn't answer this time."}
          </div>
          <button
            data-testid="chat-retry"
            onClick={onRetry}
            className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] tracking-[0.25em]"
            style={{
              background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
              color: "white",
              boxShadow: `0 6px 20px ${INDIGO}44`,
            }}
          >
            TAP TO RETRY
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full group" data-testid="chat-msg-kalki">
      <div className="text-[10px] tracking-[0.34em] opacity-60 mb-2 flex items-center gap-2" style={{ color: INDIGO }}>
        <span>✦ KALKI</span>
        {!streaming && (
          <button
            data-testid="chat-replay"
            onClick={onReplay}
            disabled={isReplaying}
            title="Hear this response"
            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 hover:text-slate-900"
            style={{ color: MUTE, fontSize: 11, letterSpacing: "0.2em" }}
          >
            {isReplaying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
            <span className="hidden sm:inline">{isReplaying ? "LOADING" : "HEAR"}</span>
          </button>
        )}
      </div>
      <div
        className="whitespace-pre-wrap"
        style={{
          color: INK,
          fontSize: "clamp(15px, 1.6vmin, 17px)",
          lineHeight: 1.7,
          letterSpacing: "0.005em",
        }}
      >
        {text}
        {streaming && (
          <span
            className="inline-block ml-1 align-middle"
            style={{ width: 8, height: 18, background: INDIGO, animation: "chat-caret 1s step-end infinite" }}
          />
        )}
      </div>
      {!streaming && vedicSky && <VedicSkyCard sky={vedicSky} />}
      {!streaming && experience?.type === "compatibility" && (
        <CompatibilityCard experience={experience} />
      )}
      {!streaming && experience && experience.type !== "compatibility" && experience.type !== "compatibility_ask" && (
        <KalkiAction experience={experience} onAccept={onAction} />
      )}

      {/* Sprint B — v0.3 Prompt 2: "Do this" practical step card. One
          non-Vedic action the user can do in 24-72h, optional Vedic
          remedy sits in a collapsed row. */}
      {!streaming && practicalStep && (
        <PracticalStepCard
          step={practicalStep}
          saved={karmaSaved}
          onAdd={onAddKarma}
          onDone={onDoneKarma}
        />
      )}

      {/* v0.6 Phase 4 — Actionable snippet card (inline, inside chat flow).
          Appears only if the backend `/kalki/respond` returned a snippet AND
          the message is not currently streaming. One card per turn. */}
      {!streaming && snippet && (
        <ChatSnippetCard
          snippet={snippet}
          onDismiss={() => onSnippetDismiss?.()}
          onSaved={() => onSnippetDismiss?.()}
        />
      )}

      {/* KALKI 5.1 — Planetary chart snapshot card.
          Rendered only for PLANETARY_OVERVIEW turns where the backend
          attached `planetary_card`.  Birth-Chart ↔ Current-Transits views;
          nothing is computed on the client. */}
      {!streaming && planetaryCard && (
        <PlanetaryChartSnapshot card={planetaryCard} />
      )}

      {/* Sprint 1 — Prompt D4: context-aware follow-up chips.
          These carry the latest domain + prediction_id so "See the timing"
          after marriage shows marriage timing, not career. */}
      {!streaming && lastDomain && onFollowup && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="chat-followups">
          {["See the timing", "Why this period?", "What should I do?"].map((label) => (
            <button
              key={label}
              data-testid={`chat-followup-${label.toLowerCase().replace(/\W+/g,"-")}`}
              onClick={() => onFollowup(label, lastDomain)}
              className="rounded-full px-3.5 py-1.5 text-[12.5px] transition hover:bg-black/5"
              style={{
                background: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(28,28,28,0.08)",
                color: INK,
                backdropFilter: "blur(6px)",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Sprint B — v0.3 Prompt 8: 👍 / 👎 reply feedback. */}
      {!streaming && onFeedback && (
        <ReplyFeedback feedback={feedback} onRate={onFeedback} />
      )}
      <style>{`@keyframes chat-caret { 50% { opacity: 0; } }`}</style>
    </div>
  );
}


/* ========== Prompt 2 — Practical Step card ========== */
function PracticalStepCard({ step, saved, onAdd, onDone }) {
  const [showRemedy, setShowRemedy] = React.useState(false);
  if (!step) return null;
  return (
    <div
      className="mt-4 rounded-2xl p-4"
      data-testid="chat-practical-step"
      style={{
        background: "linear-gradient(180deg, rgba(93,0,255,0.06), rgba(255,46,154,0.04))",
        border: "1px solid rgba(93,0,255,0.18)",
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[10px] tracking-[0.3em]" style={{ color: INDIGO }}>
          ✦ DO THIS · in {step.due_in_days || 2} day{(step.due_in_days || 2) === 1 ? "" : "s"} · ~{step.duration_min || 15} min
        </div>
        {saved && (
          <span className="text-[10px] tracking-[0.2em]" style={{ color: MUTE }}>
            ADDED TO KARMA
          </span>
        )}
      </div>
      <div style={{ color: INK, fontSize: 14, lineHeight: 1.6 }} data-testid="chat-practical-step-text">
        {step.text}
      </div>
      {!saved ? (
        <div className="mt-3 flex gap-2">
          <button
            data-testid="chat-practical-add"
            onClick={() => onAdd(step)}
            className="rounded-full px-4 py-1.5 text-[12px] tracking-[0.2em]"
            style={{
              background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
              color: "white",
            }}
          >
            ADD TO MY KARMA
          </button>
          <button
            data-testid="chat-practical-done"
            onClick={() => onDone(step)}
            className="rounded-full px-4 py-1.5 text-[12px] tracking-[0.2em]"
            style={{
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(28,28,28,0.08)",
              color: INK,
            }}
          >
            ALREADY DONE
          </button>
        </div>
      ) : null}
      {step.vedic_remedy ? (
        <div className="mt-3">
          <button
            data-testid="chat-practical-remedy-toggle"
            onClick={() => setShowRemedy((v) => !v)}
            className="text-[11px] tracking-[0.2em]"
            style={{ color: MUTE }}
          >
            {showRemedy ? "— Hide optional remedy" : "+ Optional remedy ({step.vedic_remedy.planet})".replace("{step.vedic_remedy.planet}", step.vedic_remedy.planet)}
          </button>
          {showRemedy && (
            <div className="mt-2 text-[13px]" style={{ color: INK, lineHeight: 1.6 }}>
              {step.vedic_remedy.text}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}


/* ========== Prompt 8 — Reply feedback thumbs ========== */
function ReplyFeedback({ feedback, onRate }) {
  const [showReason, setShowReason] = React.useState(false);
  const current = feedback?.rating || null;
  const reasons = [
    { id: "wrong_facts",  label: "Wrong facts" },
    { id: "too_vague",    label: "Too vague" },
    { id: "too_long",     label: "Too long" },
    { id: "not_relevant", label: "Not relevant" },
    { id: "other",        label: "Other" },
  ];
  return (
    <div className="mt-3 flex items-center gap-3" data-testid="chat-feedback">
      <button
        data-testid="chat-feedback-up"
        aria-label="Helpful"
        onClick={() => onRate("up")}
        className="rounded-full px-2 py-1 text-[14px] transition"
        style={{
          background: current === "up" ? "rgba(34,197,94,0.15)" : "transparent",
          border: `1px solid ${current === "up" ? "rgba(34,197,94,0.6)" : "rgba(28,28,28,0.1)"}`,
        }}
      >
        {current === "up" ? "👍" : "👍"}
      </button>
      <button
        data-testid="chat-feedback-down"
        aria-label="Not helpful"
        onClick={() => setShowReason(true)}
        className="rounded-full px-2 py-1 text-[14px] transition"
        style={{
          background: current === "down" ? "rgba(239,68,68,0.15)" : "transparent",
          border: `1px solid ${current === "down" ? "rgba(239,68,68,0.6)" : "rgba(28,28,28,0.1)"}`,
        }}
      >
        {current === "down" ? "👎" : "👎"}
      </button>
      {showReason && current !== "down" && (
        <div className="flex flex-wrap gap-1.5" data-testid="chat-feedback-reasons">
          {reasons.map((r) => (
            <button
              key={r.id}
              data-testid={`chat-feedback-reason-${r.id}`}
              onClick={() => { onRate("down", r.id); setShowReason(false); }}
              className="rounded-full px-2.5 py-1 text-[11px]"
              style={{
                background: "rgba(255,255,255,0.8)",
                border: "1px solid rgba(28,28,28,0.08)",
                color: INK,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========== One user message block ========== */
function UserMessage({ text }) {
  return (
    <div className="w-full flex justify-end" data-testid="chat-msg-user">
      <div
        className="rounded-2xl px-4 py-3 max-w-[80%]"
        style={{
          background: "rgba(28,28,28,0.045)",
          border: "1px solid rgba(28,28,28,0.06)",
          color: INK,
          fontSize: "clamp(15px, 1.55vmin, 16px)",
          lineHeight: 1.55,
        }}
      >
        {text}
      </div>
    </div>
  );
}

/* ========== Audio replay cache (per-message blob) ========== */
function useReplayAudio() {
  const cacheRef = useRef(new Map());          // msgId → Blob URL
  const audioRef = useRef(null);
  const [busyId, setBusyId] = useState(null);

  const ensureAudio = () => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.playsInline = true;
      el.volume = 1;
      audioRef.current = el;
    }
    return audioRef.current;
  };

  const replay = useCallback(async (msgId, text) => {
    const el = ensureAudio();
    try {
      setBusyId(msgId);
      let url = cacheRef.current.get(msgId);
      if (!url) {
        const r = await fetch(`${BACKEND_URL}/api/kalki/tts`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!r.ok) throw new Error(`tts_${r.status}`);
        const blob = await r.blob();
        if (!blob || blob.size === 0 || !(blob.type || "").startsWith("audio/")) {
          throw new Error("bad_blob");
        }
        url = URL.createObjectURL(blob);
        cacheRef.current.set(msgId, url);
      }
      el.src = url;
      await el.play();
      el.onended = () => setBusyId(null);
    } catch (e) {
      console.warn("[KALKI CHAT] replay failed", e?.message || e);
      setBusyId(null);
    }
  }, []);

  // cleanup
  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch (_) {}
    cacheRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) {} });
    cacheRef.current.clear();
  }, []);

  return { replay, busyId };
}

/* ========== The page ========== */
export default function ChatKrishna() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { user, loading } = useAuth();
  const [messages, setMessages] = useState([]);        // {id, role, text, streaming?}
  const [input, setInput] = useState("");
  // P0-5 (Build 6): hold ONE queued send if the user types + sends while a
  // reply is still streaming. Drained via effect when `thinking` returns to
  // false. Mirrored in a ref so the drain effect can read the latest value
  // without a dependency on the frequently-changing state.
  const [pendingSend, setPendingSend] = useState("");
  const pendingSendRef = useRef("");
  const [thinking, setThinking] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [newRespondBanner, setNewRespondBanner] = useState(false);
  const [credits, setCredits] = useState(null);
  const [costPerQ, setCostPerQ] = useState(10);
  const [inputFocused, setInputFocused] = useState(false);

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const streamAbortRef = useRef(null);
  const { replay, busyId: replayBusyId } = useReplayAudio();

  const conversationId = useMemo(
    () => (user ? `kalki_${user.user_id}` : "kalki_guest"),
    [user]
  );

  // When not signed in → show a visible sign-in screen on this page.
  // Replaces the old silent redirect to /app which felt like a crash on mobile.
  // We render AFTER hooks finish mounting (loading becomes false) so hook order
  // never changes between renders.

  // Fetch history + credits once authenticated
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/kalki/history");
        // Sprint v0.3 — Prompt 1: faithfully restore assistant rows including
        // chips, vedic sky, and failed/streaming status so a reload shows the
        // full thread exactly as the user left it.
        const items = (r.data?.messages || []).map((m) => {
          const orch = m?.meta?.orch || null;
          const status = m?.status || "complete";
          const isKalki = m.role === "kalki";
          const base = {
            id: m.id,
            role: m.role,
            text: m.text,
            experience: isKalki ? (orch?.suggested_experience || null) : null,
            vedicSky: isKalki ? (orch?.vedic_sky || null) : null,
            lastDomain: isKalki
              ? (orch?.vedic_sky?.topic || (orch?.signals || {})?.topic || null)
              : null,
            lastPredictionId: isKalki
              ? (orch?.vedic_sky?.timing_windows?.[0]?.prediction_id
                  || orch?.vedic_sky?.unified_prediction?.prediction_id
                  || null)
              : null,
            // Sprint B — Prompt 2: restore the "Do this" card from meta
            practicalStep: isKalki ? (m?.meta?.practical_step || null) : null,
            // Sprint B — Prompt 8: restore 👍/👎 state
            feedback: isKalki ? (m?.feedback || null) : null,
          };
          if (!isKalki) return base;
          if (status === "streaming") {
            // Server likely dropped mid-stream — show whatever text we have
            // and offer retry (originalQuestion lives in the meta.question).
            return {
              ...base,
              streaming: false,
              failed: true,
              originalQuestion: m?.meta?.question || "",
            };
          }
          if (status === "failed") {
            return {
              ...base,
              streaming: false,
              failed: true,
              originalQuestion: m?.meta?.question || "",
            };
          }
          return { ...base, streaming: false, failed: false };
        });
        setMessages(items);
        // B1 — hydrate karmaSaved flags for messages whose practical step
        // was already saved server-side. Prevents the "ADDED" button from
        // resetting after reload.
        try {
          const ks = await api.get("/kalki/karma/steps", { params: { limit: 200 } });
          const savedMsgIds = new Set(
            (ks.data?.items || [])
              .map((it) => it.message_id)
              .filter(Boolean)
          );
          if (savedMsgIds.size) {
            setMessages((all) => all.map((x) =>
              (x.serverId && savedMsgIds.has(x.serverId)) || savedMsgIds.has(x.id)
                ? { ...x, karmaSaved: true } : x
            ));
          }
        } catch (_) { /* non-fatal */ }
      } catch (e) { /* ignore */ }
      try {
        const p = await api.get("/profile");
        setCredits(p.data?.credits ?? null);
      } catch (e) { /* ignore */ }
      // Sprint B — v0.3 Prompt 7: fetch cost + balance for the credits pill
      try {
        const c = await api.get("/kalki/credits-config");
        setCostPerQ(c.data?.cost_per_question ?? 10);
        if (c.data?.balance != null) setCredits(c.data.balance);
      } catch (e) { /* ignore */ }
    })();
  }, [user]);

  // If ?prompt= is present, auto-send once after history has loaded
  const autoPromptFiredRef = useRef(false);
  useEffect(() => {
    if (!user || autoPromptFiredRef.current) return;
    const p = search.get("prompt");
    if (!p) return;
    autoPromptFiredRef.current = true;
    // Clear param from URL so refresh doesn't re-fire
    search.delete("prompt");
    setSearch(search, { replace: true });
    // small delay to let history settle
    setTimeout(() => sendMessage(p), 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-scroll logic — jump to the bottom whenever new messages arrive and
  // the user hasn't scrolled up. On first history hydration we always jump
  // to the latest message so the conversation is immediately visible.
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (!didInitialScrollRef.current && messages.length > 0) {
      el.scrollTop = el.scrollHeight;
      didInitialScrollRef.current = true;
      return;
    }
    if (autoScroll) el.scrollTop = el.scrollHeight;
  }, [messages, autoScroll]);

  // Mobile keyboard safety — visualViewport shrinks on Android/iOS when the
  // on-screen keyboard opens. We reflect that height on the page root so the
  // composer stays visible instead of being pushed off-screen.
  const [viewportH, setViewportH] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => setViewportH(Math.round(vv.height));
    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAutoScroll(nearBottom);
    if (nearBottom) setNewRespondBanner(false);
  };

  const sendMessage = useCallback(async (text, opts = {}) => {
    const q = (text ?? input).trim();
    if (!q) return;
    // P0-5 (Build 6): never silently drop a message typed while a reply is
    // streaming. If `thinking` is true, QUEUE the message instead; the queued
    // text auto-sends when the current turn settles (see effect below). The
    // input is cleared immediately so the user sees their send was accepted.
    if (thinking && !opts.retryKalkiId && !opts._fromQueue) {
      pendingSendRef.current = q;
      setPendingSend(q);
      setInput("");
      return;
    }

    // When retrying an existing failed message, reuse its id so we overwrite
    // the "I couldn't complete that thought" placeholder rather than appending.
    const userMsg = opts.retryUserId
      ? null
      : { id: `u_${Date.now().toString(36)}`, role: "user", text: q, originalText: q };
    const kalkiId = opts.retryKalkiId || `k_${Date.now().toString(36)}`;
    const kalkiMsg = opts.retryKalkiId
      ? null
      : { id: kalkiId, role: "kalki", text: "", streaming: true, originalQuestion: q };

    setMessages((m) => {
      if (opts.retryKalkiId) {
        // Flip the failed message back to streaming
        return m.map((x) => x.id === kalkiId ? { ...x, text: "", streaming: true, failed: false } : x);
      }
      return [...m, userMsg, kalkiMsg];
    });
    if (!opts.retryKalkiId) setInput("");
    setThinking(true);
    setAutoScroll(true);

    try { streamAbortRef.current?.abort(); } catch (_) {}

    // Sprint 1 — Prompt D1: never silently lose a reply. Retry up to 2 times
    // with exponential backoff. If all attempts fail, mark the message as
    // `failed:true` so the UI can show a "Tap to retry" chip.
    const MAX_ATTEMPTS = 3;
    let attempt = 0;
    let lastErr = null;
    while (attempt < MAX_ATTEMPTS) {
      attempt++;
      const ctrl = new AbortController();
      streamAbortRef.current = ctrl;
      try {
        const r = await fetch(`${BACKEND_URL}/api/kalki/respond/stream`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
          body: JSON.stringify({
            text: q, conversationId, mode: "chat",
            // Pass carry-forward context so follow-up chips land on the SAME
            // domain / prediction as the latest Krishna reply.
            lastDomain: opts.lastDomain || undefined,
            lastPredictionId: opts.lastPredictionId || undefined,
            attempt,
          }),
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) throw new Error(`stream_${r.status}`);
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let full = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const chunk of parts) {
            const line = chunk.trim();
            if (!line.startsWith("data:")) continue;
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.delta) {
                full += data.delta;
                setMessages((m) => m.map((x) => x.id === kalkiId ? { ...x, text: full } : x));
                const el = listRef.current;
                if (el && (el.scrollHeight - el.scrollTop - el.clientHeight > 120)) {
                  setNewRespondBanner(true);
                }
              } else if (data.done) {
                setMessages((m) => m.map((x) => x.id === kalkiId ? {
                  ...x,
                  // Sprint v0.3 — Prompt 1: use the server messageId so
                  // feedback / karma mutations land on the persisted row.
                  serverId: data.messageId || x.serverId || null,
                  text: data.reply || full,
                  streaming: false,
                  failed: false,
                  experience: data.suggested_experience || null,
                  vedicSky: data.vedic_sky || null,
                  // Sprint 1 — Prompt D4: remember domain + prediction_id so
                  // follow-up chips land on the same thread.
                  lastDomain: data.vedic_sky?.topic || data.topic || null,
                  lastPredictionId:
                    (data.vedic_sky?.timing_windows?.[0]?.prediction_id) ||
                    (data.vedic_sky?.unified_prediction?.prediction_id) || null,
                  // Sprint B — v0.3 Prompt 2: expose the "Do this" card
                  practicalStep: data.practical_step || null,
                  karmaSaved: false,
                  // v0.6 Phase 4 — actionable snippet surfaced in-chat
                  snippet: data.snippet || null,
                  // KALKI 5.1 — Planetary Conversational Intelligence card.
                  planetaryCard: data.planetary_card || null,
                } : x));
                // v0.6 Phase 4 — Blessing popup trigger (ignore null)
                if (data.blessing && data.blessing.ok) {
                  window.dispatchEvent(new CustomEvent("kalki:blessing-granted",
                    { detail: data.blessing }));
                }
                // v0.6 Phase 4 — Character milestone acknowledgment (quiet toast)
                if (data.character_milestone && data.character_milestone.message) {
                  toast(data.character_milestone.message, { duration: 6000 });
                }
                // Sprint B — v0.3 Prompt 7: debit the local credits pill
                if (typeof data.charged === "number" && data.charged > 0) {
                  setCredits((c) => (c == null ? c : Math.max(0, c - data.charged)));
                }
              } else if (data.error) {
                throw new Error(data.error);
              }
            } catch (_) {}
          }
        }
        setMessages((m) => m.map((x) => x.id === kalkiId ? { ...x, streaming: false, failed: false } : x));
        setThinking(false);
        streamAbortRef.current = null;
        return; // success
      } catch (e) {
        lastErr = e;
        console.warn(`[KALKI CHAT] stream attempt ${attempt}/${MAX_ATTEMPTS} failed`, e?.message || e);
        if (attempt < MAX_ATTEMPTS) {
          const delay = 400 * Math.pow(2, attempt - 1); // 400, 800, 1600
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }
      }
    }

    // All attempts failed
    setMessages((m) => m.map((x) =>
      x.id === kalkiId
        ? { ...x, text: "", streaming: false, failed: true, originalQuestion: q }
        : x
    ));
    setThinking(false);
    streamAbortRef.current = null;
  }, [input, thinking, conversationId]);

  // Sprint 1 — Prompt D1: user-tappable retry.
  const retryMessage = useCallback((failedMsg) => {
    if (!failedMsg?.originalQuestion) return;
    sendMessage(failedMsg.originalQuestion, { retryKalkiId: failedMsg.id });
  }, [sendMessage]);

  // P0-5 drain: when streaming ends, auto-flush the queued message so a
  // mid-stream type-and-send is honoured. Only drains if `pendingSend` is
  // non-empty AND we're no longer thinking. The _fromQueue flag bypasses
  // the thinking guard in sendMessage so a race cannot re-queue it.
  useEffect(() => {
    if (!thinking && pendingSendRef.current) {
      const q = pendingSendRef.current;
      pendingSendRef.current = "";
      setPendingSend("");
      // Small defer so the previous turn's state settles cleanly.
      const t = setTimeout(() => { sendMessage(q, { _fromQueue: true }); }, 50);
      return () => clearTimeout(t);
    }
  }, [thinking, sendMessage]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Chip dispatcher — routes experience taps.
  const handleAction = useCallback((exp) => {
    if (!exp) return;
    const kind = exp.kind;
    if (kind === "ask_vedic") {
      // Route to main dashboard where the Devotee profile / birth details live.
      navigate("/app");
      return;
    }
    const promptByKind = {
      sankalp:    "Yes — I'm ready. Help me shape this into my Sankalp.",
      karma:      "What is the one Karma I should take today?",
      prasad:     "I want to offer Prasad for this moment.",
      blessing:   "I would like to receive a blessing.",
      reflection: "Help me reflect on what I'm feeling.",
      reveal_prediction: "Yes — show me what the timing may hold.",
      why:        "Why do you see this? Walk me through it.",
    };
    const msg = promptByKind[kind];
    if (msg) sendMessage(msg);
  }, [navigate, sendMessage]);

  const openerPrompt = (chip) => {
    const map = {
      "Life": "I want to talk about my life right now.",
      "Career": "I'm thinking about my career.",
      "Love": "Let's talk about love.",
      "Money": "I'm thinking about money and abundance.",
      "Purpose": "What is my purpose?",
      "What's ahead?": "Tell me what may lie ahead.",
    };
    sendMessage(map[chip] || chip);
  };

  // Sprint 1 — Prompt D4: Context-aware follow-up chips. When Krishna's last
  // answer was about marriage, "See the timing" must retrieve marriage timing
  // — not career. We carry the last domain + prediction_id forward.
  const handleFollowup = useCallback((label, lastDomain) => {
    const domainWord = (lastDomain || "this area").replace(/_/g, " ");
    const promptByLabel = {
      "See the timing":  `Show me the timing window for my ${domainWord} question.`,
      "Why this period?":`Why this specific period for my ${domainWord} question? What in my chart points to it?`,
      "What should I do?":`What is one practical thing I should do in this ${domainWord} window?`,
    };
    const msg = promptByLabel[label] || label;
    sendMessage(msg, { lastDomain });
  }, [sendMessage]);

  // Sprint B — v0.3 Prompt 2: Save the practical step to Karma.
  const handleAddKarma = useCallback(async (msg, step) => {
    if (!step) return;
    try {
      await api.post("/kalki/karma/steps", {
        message_id: msg?.serverId || msg?.id,
        text: step.text,
        category: step.category,
        due_in_days: step.due_in_days,
        fingerprint: step.fingerprint,
        vedic_remedy: step.vedic_remedy || null,
      });
      setMessages((all) => all.map((x) => x.id === msg.id ? { ...x, karmaSaved: true } : x));
    } catch (e) {
      console.warn("[KALKI CHAT] add karma failed", e?.message || e);
    }
  }, []);

  const handleDoneKarma = useCallback(async (msg, step) => {
    // "Already done" path — persist as done without an intermediate active row.
    if (!step) return;
    try {
      const r = await api.post("/kalki/karma/steps", {
        message_id: msg?.serverId || msg?.id,
        text: step.text, category: step.category,
        due_in_days: step.due_in_days, fingerprint: step.fingerprint,
      });
      const id = r?.data?.id;
      if (id) await api.post(`/kalki/karma/steps/${id}/done`);
      setMessages((all) => all.map((x) => x.id === msg.id ? { ...x, karmaSaved: true } : x));
    } catch (e) {
      console.warn("[KALKI CHAT] mark done failed", e?.message || e);
    }
  }, []);

  // Sprint B — v0.3 Prompt 8: thumb feedback.
  const handleFeedback = useCallback(async (msg, rating, reason = null) => {
    try {
      await api.post("/kalki/feedback", {
        message_id: msg?.serverId || msg?.id,
        rating, reason,
      });
      setMessages((all) => all.map((x) => x.id === msg.id ? {
        ...x, feedback: { rating, reason },
      } : x));
    } catch (e) {
      console.warn("[KALKI CHAT] feedback failed", e?.message || e);
    }
  }, []);

  const empty = messages.length === 0;
  const krishnaStatus = thinking ? "thinking" : "idle";

  // Mobile fix (Oct 2026) — show a clear sign-in screen instead of silently
  // redirecting back to /app, which felt like Chat was "not opening".
  if (!loading && !user) {
    return (
      <SignInRequired
        variant="light"
        title="Sign in to Chat with KALKI"
        subtitle="Chat is private to you — a quick sign-in keeps your conversation safe and lets KALKI remember your path."
      />
    );
  }

  return (
    <div
      className="relative w-full text-slate-900 flex flex-col"
      style={{
        height: viewportH ? `${viewportH}px` : "100dvh",
        minHeight: "100svh",
        overflow: "hidden",
        background: "#FAFAFC",
        backgroundImage: "radial-gradient(#DCE2EA 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}
      data-testid="chat-krishna"
    >
      {/* HEADER — unified KalkiGlobalHeader (Website 6.0) */}
      <KalkiGlobalHeader />

      {/* MOBILE Talk shortcut (top right secondary) */}
      <button
        data-testid="chat-mobile-talk"
        onClick={() => navigate("/app/talk")}
        className="md:hidden absolute top-5 right-20 z-20 text-[11px] tracking-[0.3em] flex items-center gap-1"
        style={{ color: MUTE }}
      >
        <Mic className="w-3.5 h-3.5" /> TALK
      </button>

      {/* MAIN — flexes to fill remaining viewport. ConversationColumn
          owns its own internal scroll + sticky composer. */}
      <main
        className="relative z-10 flex flex-col w-full"
        style={{ flex: "1 1 auto", minHeight: 0 }}
      >
        <ConversationColumn
          empty={empty}
          messages={messages}
          listRef={listRef}
          onScroll={handleScroll}
          newRespondBanner={newRespondBanner}
          onScrollToLatest={() => {
            setAutoScroll(true);
            setNewRespondBanner(false);
            const el = listRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          }}
          input={input}
          setInput={setInput}
          onSend={() => sendMessage()}
          onMic={() => navigate("/app/talk")}
          onKey={onKey}
          inputRef={inputRef}
          onOpenerClick={openerPrompt}
          onRetry={retryMessage}
          onFollowup={handleFollowup}
          thinking={thinking}
          replay={replay}
          replayBusyId={replayBusyId}
          onAction={handleAction}
          setInputFocused={setInputFocused}
          onAddKarma={handleAddKarma}
          onDoneKarma={handleDoneKarma}
          onFeedback={handleFeedback}
          onSnippetDismiss={(mid) => setMessages((prev) => prev.map((x) => x.id === mid ? { ...x, snippet: null } : x))}
          pendingSend={pendingSend}
          credits={credits}
          costPerQuestion={costPerQ}
          testIdSuffix=""
        />
      </main>
    </div>
  );
}

function ConversationColumn({
  empty, messages, listRef, onScroll, newRespondBanner, onScrollToLatest,
  input, setInput, onSend, onMic, onKey, inputRef, onOpenerClick, thinking,
  replay, replayBusyId, onAction, onRetry, onFollowup, setInputFocused,
  onAddKarma, onDoneKarma, onFeedback, credits, costPerQuestion,
  onSnippetDismiss, pendingSend,
  testIdSuffix = "",
}) {
  return (
    <section
      className="flex flex-col w-full mx-auto px-4 sm:px-8"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxWidth: 860,
        width: "100%",
      }}
    >
      {/* Messages scroll area (ONLY this scrolls) */}
      <div
        ref={listRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        style={{ paddingTop: 12, paddingBottom: 24, overscrollBehavior: "contain" }}
        data-testid="chat-scroll"
      >
        {empty ? (
          <EmptyOpener onOpenerClick={onOpenerClick} />
        ) : (
          <div className="flex flex-col gap-7 pb-6">
            {messages.map((m) =>
              m.role === "user" ? (
                <UserMessage key={m.id} text={m.text} />
              ) : (
                <KalkiMessage
                  key={m.id}
                  text={m.text || (thinking && !m.streaming ? "✦ Understanding…" : "")}
                  streaming={!!m.streaming}
                  failed={!!m.failed}
                  isReplaying={replayBusyId === m.id}
                  onReplay={() => replay(m.id, m.text)}
                  onRetry={() => onRetry?.(m)}
                  experience={m.experience}
                  vedicSky={m.vedicSky}
                  onAction={onAction}
                  lastDomain={m.lastDomain}
                  onFollowup={onFollowup}
                  practicalStep={m.practicalStep}
                  karmaSaved={m.karmaSaved}
                  onAddKarma={(step) => onAddKarma?.(m, step)}
                  onDoneKarma={(step) => onDoneKarma?.(m, step)}
                  feedback={m.feedback}
                  onFeedback={(rating, reason) => onFeedback?.(m, rating, reason)}
                  snippet={m.snippet}
                  onSnippetDismiss={() => onSnippetDismiss?.(m.id)}
                  planetaryCard={m.planetaryCard}
                />
              )
            )}
            {thinking && !messages.some((m) => m.streaming) && (
              <div className="text-[11px] tracking-[0.34em]" style={{ color: INDIGO }}>
                ✦ UNDERSTANDING…
              </div>
            )}
          </div>
        )}
      </div>

      {/* New-response banner */}
      {newRespondBanner && (
        <button
          onClick={onScrollToLatest}
          className="absolute left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-[11px] tracking-[0.3em]"
          style={{
            bottom: 110,
            background: "white", color: INK,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            border: `1px solid ${INDIGO}44`,
          }}
          data-testid="chat-new-banner"
        >
          ↓ NEW RESPONSE
        </button>
      )}

      {/* Composer — flex-shrink 0 so it never collapses, sticks to bottom
          of the column. Honors mobile safe-area + Android keyboard inset. */}
      <div
        className="shrink-0 w-full"
        style={{
          flex: "0 0 auto",
          padding: "12px 0 16px",
          paddingBottom: `calc(16px + env(safe-area-inset-bottom, 0px))`,
          background: "linear-gradient(to top, #FAFAFC 72%, rgba(250,250,252,0))",
          zIndex: 5,
        }}
      >
        {/* Sprint B — v0.3 Prompt 7: cost + balance pill above the composer. */}
        {costPerQuestion > 0 && (
          <div
            className="relative mx-auto mb-2 flex items-center justify-between"
            style={{ maxWidth: 760, padding: "0 8px" }}
            data-testid="chat-credits-pill"
          >
            <div className="text-[10.5px] tracking-[0.22em]" style={{ color: "#A1A1AA" }}>
              {costPerQuestion} CREDITS PER QUESTION
            </div>
            <div
              className="text-[10.5px] tracking-[0.22em]"
              data-testid="chat-credits-balance"
              style={{ color: (credits ?? 0) < costPerQuestion ? "#D04A4A" : "#5D00FF" }}
            >
              {credits == null ? "— credits" : `${credits} CREDITS`}
            </div>
          </div>
        )}
        <div
          className="relative mx-auto"
          style={{
            maxWidth: 760,
            background: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(28,28,28,0.08)",
            borderRadius: 22,
            boxShadow: "0 10px 40px rgba(28,28,28,0.06)",
            padding: "10px 10px 10px 18px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <textarea
            ref={inputRef}
            data-testid={`chat-input${testIdSuffix}`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            onFocus={() => setInputFocused?.(true)}
            onBlur={() => setInputFocused?.(false)}
            rows={1}
            placeholder={thinking && pendingSend ? "Queued — will send after reply…"
                        : thinking ? "KALKI is answering — you can type the next one"
                        : "Talk to me about anything..."}
            className="flex-1 bg-transparent resize-none outline-none text-[15px] leading-[1.5] py-2"
            style={{ color: INK, maxHeight: 120 }}
          />
          <button
            data-testid={`chat-mic${testIdSuffix}`}
            onClick={onMic}
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition"
            aria-label="Switch to voice"
            title="Voice conversation"
          >
            <Mic className="w-5 h-5" style={{ color: MUTE }} />
          </button>
          <button
            data-testid={`chat-send${testIdSuffix}`}
            onClick={onSend}
            disabled={!input.trim()}
            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`, boxShadow: `0 6px 20px ${INDIGO}55` }}
            aria-label={thinking ? "Queue message" : "Send"}
            title={thinking ? "Queue — will send after KALKI's reply" : "Send"}
          >
            {thinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </section>
  );
}

function EmptyOpener({ onOpenerClick }) {
  const [today, setToday] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/kalki/today")
      .then(r => { if (!cancelled) setToday(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="pt-6 sm:pt-10 px-1">
      <div className="text-[11px] tracking-[0.4em]" style={{ color: INDIGO }}>KALKI</div>
      <h1
        className="font-light mt-3 leading-[1.1]"
        style={{
          color: INK,
          fontSize: "clamp(28px, 4.6vmin, 56px)",
          letterSpacing: "-0.01em",
        }}
      >
        What would you like
        <br /> to talk about?
      </h1>
      <p className="mt-4 text-[14px] sm:text-[15px]" style={{ color: MUTE, maxWidth: "42ch" }}>
        I’m here. Tell me what’s on your mind.
      </p>
      {/* Sprint 3 — Prompt D5: "Today with Krishna" — personalized action
          card grounded in current dasha + latest open karma. */}
      {today?.action && (
        <div
          data-testid="chat-today-card"
          className="mt-6 rounded-2xl relative overflow-hidden"
          style={{
            maxWidth: 520,
            padding: "16px 20px",
            background: "radial-gradient(120% 90% at 15% 0%, rgba(93,0,255,0.14) 0%, rgba(93,0,255,0) 55%), rgba(255,255,255,0.82)",
            border: "1px solid rgba(93,0,255,0.14)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 10px 30px rgba(30,0,80,0.06)",
          }}
        >
          <div style={{ color: INDIGO, fontSize: 10, letterSpacing: "0.34em", textTransform: "uppercase" }}>
            Today with KALKI
          </div>
          <div style={{ color: INK, fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: 20, marginTop: 6, lineHeight: 1.2 }}>
            {today.headline}
          </div>
          <div style={{ color: MUTE, fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
            {today.action}
          </div>
        </div>
      )}
      <div className="mt-7 flex flex-wrap gap-2.5">
        {OPENER_CHIPS.map((c) => (
          <button
            key={c}
            data-testid={`chat-opener-${c.toLowerCase().replace(/\W+/g, "-")}`}
            onClick={() => onOpenerClick(c)}
            className="rounded-full px-4 py-2 text-[13px] font-medium transition hover:bg-black/5"
            style={{
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(28,28,28,0.08)",
              color: INK,
              backdropFilter: "blur(8px)",
            }}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}
